import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ArrowLeft, Plus, BookOpen, X, CheckCircle2 } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import {
  getFieldLogEntries,
  createFieldLogEntry,
} from '../../services/api'

export default function FieldLog({ ranchId }) {
  const queryClient = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)

  const { data: entries, isLoading } = useQuery({
    queryKey: ['fieldLogEntries', ranchId],
    queryFn: () => getFieldLogEntries(ranchId),
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/land" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Field Log</h1>
            <p className="text-gray-500 mt-1">
              Daily paper-log transcription. Page photo + 2-witness signoff per drone-operator §4.4.
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="btn bg-green-600 text-white hover:bg-green-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" /> New Entry
        </button>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Loading…</div>
        ) : !entries?.length ? (
          <div className="p-8 text-center text-gray-400">
            No field-log entries yet. The bound paper notebook is the source — this is the index.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Page #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Witness</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Summary</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Photo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {entries.map(e => (
                <tr key={e.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">{format(parseISO(e.entry_date), 'dd MMM yyyy')}</td>
                  <td className="px-4 py-3 text-sm font-mono">{e.page_number || '—'}</td>
                  <td className="px-4 py-3 text-sm">
                    {e.witness_role ? (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700">
                        {e.witness_role}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 max-w-md truncate">
                    {e.summary_text || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {e.page_photo_media_id ? (
                      <span className="text-green-600 inline-flex items-center gap-1">
                        <BookOpen className="h-4 w-4" /> uploaded
                      </span>
                    ) : (
                      <span className="text-gray-400">missing</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreateModal
          ranchId={ranchId}
          onClose={() => setShowCreate(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['fieldLogEntries'] })
            setShowCreate(false)
          }}
        />
      )}
    </div>
  )
}

function CreateModal({ ranchId, onClose, onSuccess }) {
  const [form, setForm] = useState({
    entry_date: new Date().toISOString().slice(0, 10),
    page_number: '',
    witness_role: '',
    summary_text: '',
    notes: '',
  })
  const [error, setError] = useState(null)
  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  const mutation = useMutation({
    mutationFn: () => createFieldLogEntry({
      ranch_id: ranchId,
      entry_date: form.entry_date,
      page_number: form.page_number ? parseInt(form.page_number, 10) : null,
      witness_role: form.witness_role || null,
      summary_text: form.summary_text || null,
      notes: form.notes || null,
    }),
    onSuccess,
    onError: (err) => setError(err.response?.data?.detail || 'Failed to save field-log entry'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-green-600" /> New Field-Log Entry
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); mutation.mutate() }}
          className="p-4 space-y-4"
        >
          {error && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Date *</label>
              <input type="date" className="input w-full" value={form.entry_date}
                onChange={e => update('entry_date', e.target.value)} required />
            </div>
            <div>
              <label className="label">Page #</label>
              <input type="number" min="1" className="input w-full" value={form.page_number}
                onChange={e => update('page_number', e.target.value)} placeholder="42" />
            </div>
          </div>

          <div>
            <label className="label">Witness Role</label>
            <select className="input w-full"
              value={form.witness_role} onChange={e => update('witness_role', e.target.value)}>
              <option value="">— none —</option>
              <option value="manager">Manager</option>
              <option value="biochar_operator">Biochar operator</option>
              <option value="bush_clearer">Bush clearer</option>
              <option value="visitor">Visitor</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              The second signatory's role. Use the &quot;manage users&quot; flow to add a witness_user_id later.
            </p>
          </div>

          <div>
            <label className="label">Summary</label>
            <textarea className="input w-full" rows={4} value={form.summary_text}
              onChange={e => update('summary_text', e.target.value)}
              placeholder="Transcription of today's notebook page" />
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea className="input w-full" rows={2} value={form.notes}
              onChange={e => update('notes', e.target.value)} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn bg-gray-100 text-gray-700 hover:bg-gray-200">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending}
              className="btn bg-green-600 text-white hover:bg-green-700 flex items-center gap-2">
              {mutation.isPending ? 'Saving…' : (<><CheckCircle2 className="h-4 w-4" /> Save</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
